import {
  API_VERSION,
  HDR_API_VERSION,
  type PwnedRangeBatchRequest,
  type PwnedRangeBatchResponse,
} from '@heybesafe/shared';
import { resolveApiBaseUrl } from './apiBaseUrl';

/**
 * Fetch HIBP Pwned Passwords ranges for a batch of 5-char k-anonymity prefixes,
 * via our first-party `POST /pwned-range` proxy (body `{ prefixes }`, ≤
 * `PWNED_RANGE_BATCH_SIZE`). Returns prefix (upper-cased) → range text, where a
 * `null` value means that one prefix's upstream fetch failed. A prefix missing
 * from the response is reported as `null` too. This does NOT go through the
 * generic `request<T>` helper: the route is public (no `Authorization`).
 *
 * **Fails soft** — any non-ok status (a WAF 429 included), malformed body, or
 * thrown error returns `null` for the whole batch, so the checkup shows
 * "couldn't check" rather than a false "safe".
 */
const API_BASE_URL = resolveApiBaseUrl(
  window.location.hostname,
  (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL,
);

export async function fetchPwnedRanges(
  prefixes: ReadonlyArray<string>,
): Promise<Map<string, string | null> | null> {
  try {
    const body: PwnedRangeBatchRequest = { prefixes: [...prefixes] };
    const res = await fetch(`${API_BASE_URL}/pwned-range`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Platform': 'web',
        [HDR_API_VERSION]: String(API_VERSION),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const json = (await res.json()) as Partial<PwnedRangeBatchResponse> | null;
    const ranges = json?.ranges;
    if (!ranges || typeof ranges !== 'object') return null;
    const out = new Map<string, string | null>();
    for (const p of prefixes) {
      const v = ranges[p.toUpperCase()];
      out.set(p, typeof v === 'string' ? v : null);
    }
    return out;
  } catch {
    return null;
  }
}
